import React, { useEffect, useState } from "react";
import { Card } from "react-bootstrap";
import { Link } from "react-router-dom";
import PropTypes from "prop-types";

import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import usePermissions from "hooks/usePermissions";
import Button from "components/atoms/Button/Button";
import testIds from "constants/testIds";
import { FormattedMessage, useIntl } from "react-intl";
import { useGetWidgets } from "api/queries/user/useGetWidgets";
import { getWidgetStatusesTranslations } from "helpers/translationHelpers";

const StatusWidget = function ({
  widgetContent,
  paymentLocation,
  isLayoutLocked,
  isPreview,
  widgetId,
  handleWidgetDialogOpen,
  refreshToken,
}) {
  const intl = useIntl();
  const { permissions } = usePermissions();
  const [widget, setWidget] = useState({});
  const [paymentLocationLabel, setPaymentLocationLabel] = useState();

  const { data: statusWidgetData, isFetching } = useGetWidgets({
    paymentLocation: paymentLocation !== "All" ? paymentLocation : null,
    refreshToken,
  });

  useEffect(() => {
    const paymentLocations = JSON.parse(permissions || "{}")?.payment_location?.locations;
    setPaymentLocationLabel(paymentLocations?.find((pl) => pl.value === paymentLocation)?.label);

    if (!isFetching && statusWidgetData?.widgets) {
      const widget = statusWidgetData?.widgets.find((widget) => widget.label.toLowerCase() === widgetContent.toLowerCase());
      setWidget(widget);
    }
  }, [isFetching, widgetContent, statusWidgetData, permissions, paymentLocation]);

  const generateLinkTo = () => {
    switch (widget?.label) {
      case "Shipments, in transit":
        return "/shipments?display_status=In transit";
      case "Incomplete shipments":
        return "/shipments?display_status=Incomplete";
      case "Booked, year to date":
        return "/analytics?filter=location&report=count";
      case "Processed Shipments":
        return "/shipments?display_status=Booked";
      case "Delivered Shipments":
        return "/shipments?display_status=Delivered";
      case "Delayed Shipments":
        return "/shipments?display_status=Delayed";
      case "Open quote requests":
        return "/quote-requests?display_status=open_for_quotations";
      case "At risk for cancel":
        return "/quote-requests?display_status=archived";
      case "Awaiting booking":
        return "/quote-requests?display_status=awaiting_action";
      case "Awaiting approval":
        return "/quote-requests?display_status=approval_pending";
      case "Open quick requests":
        return "/quick-requests?display_status=pending";
      case "Awaiting audit":
        return "/invoices?display_status=awaiting_audit";
      case "Pending Invoices":
        return "/invoices?display_status=pending"; // TODO: Update URL when the provider invoice list is created
      case "Pending Tracking Number":
        return "/shipments"; // TODO: Update URL when the provider shipment list is created
      case "Pending POD shipments":
        return "/shipments"; // TODO: Update URL when the provider shipment list is created
      default:
        return "/";
    }
  };

  if (!isFetching && statusWidgetData?.widgets) {
    return (
      <Card.Body className="d-flex flex-column justify-content-end user-select-none pl-16 pt-12 pb-14 pr-16">
        <div className="d-flex justify-content-between align-items-center">
          <Link
            onClick={(e) => {
              if (!isLayoutLocked || isPreview) e.preventDefault();
            }}
            to={generateLinkTo()}
            className={`text-decoration-none ${!isLayoutLocked || isPreview ? "cursor-default" : ""}`}
          >
            <h2 className="text-primary">{widget?.value}</h2>
          </Link>
          {!isLayoutLocked && !isPreview && (
            <Button
              className="edit-btn"
              onClick={() => {
                handleWidgetDialogOpen(widgetId);
              }}
              data-testid={`${testIds.EDIT_WIDGET_BUTTON}-${widgetId}`}
            >
              <FormattedMessage id="general.edit" defaultMessage="Edit" />
            </Button>
          )}
        </div>
        <Link
          onClick={(e) => {
            if (!isLayoutLocked || isPreview) {
              e.preventDefault();
            }
          }}
          to={generateLinkTo()}
          className={`text-decoration-none ${!isLayoutLocked || isPreview ? "cursor-default" : ""}`}
        >
          <div className="d-flex justify-content-between">
            <p className="font-b3 text-secondary">
              {widget?.label && getWidgetStatusesTranslations(widget?.label, intl)}{" "}
              {paymentLocation && paymentLocation !== "All" && (
                <span className="text-secondary">({paymentLocationLabel})</span>
              )}
            </p>
          </div>
        </Link>
      </Card.Body>
    );
  }
  return (
    <Card.Body className="overflow-hidden" style={{ transform: "translateY(-15px)" }}>
      <LoadingSkeleton rows={1} className="pl-16 pr-16" isListing />
    </Card.Body>
  );
};

StatusWidget.propTypes = {
  widgetContent: PropTypes.string.isRequired,
  paymentLocation: PropTypes.string,
  isLayoutLocked: PropTypes.bool,
  isPreview: PropTypes.bool,
  widgetId: PropTypes.number,
  handleWidgetDialogOpen: PropTypes.func,
  refreshToken: PropTypes.bool.isRequired,
};

StatusWidget.defaultProps = {
  isLayoutLocked: false,
  paymentLocation: null,
  isPreview: false,
  widgetId: null,
  handleWidgetDialogOpen: () => {},
};

export default StatusWidget;
